import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { apiUrl } from "../config/api";

type SaleChannelItem = {
  id: number;
  code: string;
  label: string;
  is_active: boolean;
};

type SaleChannelsResponse = {
  ok?: boolean;
  items?: unknown;
  item?: unknown;
  error?: string;
};

type CreateForm = {
  code: string;
  label: string;
  is_active: boolean;
};

const TOKEN_STORAGE_KEY = "bg_shop_token";
const SALE_CHANNELS_API = apiUrl("/sale-channels");

const INITIAL_FORM: CreateForm = {
  code: "",
  label: "",
  is_active: true,
};

function toSaleChannelItem(row: unknown): SaleChannelItem | null {
  if (!row || typeof row !== "object") return null;
  const data = row as Record<string, unknown>;
  const id = Number(data.id);
  if (!Number.isInteger(id) || id <= 0) return null;
  if (typeof data.code !== "string" || !data.code.trim()) return null;
  if (typeof data.label !== "string" || !data.label.trim()) return null;

  return {
    id,
    code: data.code.trim(),
    label: data.label.trim(),
    is_active: Boolean(data.is_active),
  };
}

function getErrorMessage(code: string): string {
  switch (code) {
    case "sale_channel_code_required":
      return "Please enter a code for this sale location.";
    case "sale_channel_label_required":
      return "Please enter a display name for this sale location.";
    case "invalid_sale_channel_code":
      return "Code can only use A-Z, 0-9, and underscore.";
    case "sale_channel_duplicate":
      return "This sale location code already exists.";
    case "sale_channel_not_found":
      return "The selected sale location was not found.";
    case "forbidden":
      return "This account does not have permission to manage sale locations.";
    case "missing_auth_token":
    case "invalid_auth_token":
      return "Please log in again before managing sale locations.";
    case "failed_to_fetch_sale_channels":
      return "Unable to load sale locations right now.";
    case "failed_to_create_sale_channel":
      return "Unable to create this sale location right now.";
    case "failed_to_update_sale_channel":
      return "Unable to update this sale location right now.";
    default:
      return "Something went wrong while managing sale locations.";
  }
}

export default function SaleChannelsPage(): JSX.Element {
  const [items, setItems] = useState<SaleChannelItem[]>([]);
  const [form, setForm] = useState<CreateForm>(INITIAL_FORM);
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [successMessage, setSuccessMessage] = useState("");
  const [updatingId, setUpdatingId] = useState<number | null>(null);
  const [drafts, setDrafts] = useState<Record<number, CreateForm>>({});

  useEffect(() => {
    void loadSaleChannels();
  }, []);

  // PMN-CRITICAL(/SaleChannelsPage): Sale channel code is the stable value saved into orders.sale_channel and used by ScanPage/report filters.
  async function loadSaleChannels(): Promise<void> {
    const token = localStorage.getItem(TOKEN_STORAGE_KEY) ?? "";
    if (!token) {
      setError("Please log in again before managing sale locations.");
      setLoading(false);
      return;
    }

    setLoading(true);
    try {
      const resp = await fetch(SALE_CHANNELS_API, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      const data = (await resp.json().catch(() => ({}))) as SaleChannelsResponse;
      if (!resp.ok || !data.ok || !Array.isArray(data.items)) {
        throw new Error(data.error ?? "failed_to_fetch_sale_channels");
      }

      const nextItems = data.items
        .map(toSaleChannelItem)
        .filter((item): item is SaleChannelItem => item != null);

      setItems(nextItems);
      setDrafts(
        nextItems.reduce<Record<number, CreateForm>>((acc, item) => {
          acc[item.id] = {
            code: item.code,
            label: item.label,
            is_active: item.is_active,
          };
          return acc;
        }, {})
      );
      setError("");
    } catch (err) {
      const code = err instanceof Error ? err.message : "failed_to_fetch_sale_channels";
      setError(getErrorMessage(code));
    } finally {
      setLoading(false);
    }
  }

  // PMN-CRITICAL(/SaleChannelsPage): Creating a channel changes master options for user assignment and ScanPage allowed sale channels.
  async function handleCreate(e: React.FormEvent<HTMLFormElement>): Promise<void> {
    e.preventDefault();
    setError("");
    setSuccessMessage("");

    const token = localStorage.getItem(TOKEN_STORAGE_KEY) ?? "";
    if (!token) {
      setError("Please log in again before managing sale locations.");
      return;
    }

    const code = form.code.trim().toUpperCase();
    const label = form.label.trim();

    if (!code) {
      setError("Please enter a code for this sale location.");
      return;
    }
    if (!label) {
      setError("Please enter a display name for this sale location.");
      return;
    }

    setSaving(true);
    try {
      const resp = await fetch(SALE_CHANNELS_API, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          code,
          label,
          is_active: form.is_active,
        }),
      });

      const data = (await resp.json().catch(() => ({}))) as SaleChannelsResponse;
      if (!resp.ok || !data.ok) {
        throw new Error(data.error ?? "failed_to_create_sale_channel");
      }

      setForm(INITIAL_FORM);
      setSuccessMessage(`Sale location ${code} was created successfully.`);
      await loadSaleChannels();
    } catch (err) {
      const code = err instanceof Error ? err.message : "failed_to_create_sale_channel";
      setError(getErrorMessage(code));
    } finally {
      setSaving(false);
    }
  }

  // PMN-CRITICAL(/SaleChannelsPage): Renaming code does not migrate historical orders.sale_channel values; treat code edits as business-critical.
  async function handleSave(item: SaleChannelItem): Promise<void> {
    const token = localStorage.getItem(TOKEN_STORAGE_KEY) ?? "";
    if (!token) {
      setError("Please log in again before managing sale locations.");
      return;
    }

    const draft = drafts[item.id];
    if (!draft) return;

    const code = draft.code.trim().toUpperCase();
    const label = draft.label.trim();

    if (!code) {
      setError("Please enter a code for this sale location.");
      return;
    }
    if (!label) {
      setError("Please enter a display name for this sale location.");
      return;
    }

    setUpdatingId(item.id);
    setError("");
    setSuccessMessage("");

    try {
      const resp = await fetch(apiUrl(`/sale-channels/${item.id}`), {
        method: "PATCH",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          code,
          label,
          is_active: draft.is_active,
        }),
      });

      const data = (await resp.json().catch(() => ({}))) as SaleChannelsResponse;
      if (!resp.ok || !data.ok || !data.item) {
        throw new Error(data.error ?? "failed_to_update_sale_channel");
      }

      const nextItem = toSaleChannelItem(data.item);
      if (!nextItem) {
        throw new Error("failed_to_update_sale_channel");
      }

      setItems((prev) => prev.map((row) => (row.id === item.id ? nextItem : row)));
      setDrafts((prev) => ({
        ...prev,
        [item.id]: {
          code: nextItem.code,
          label: nextItem.label,
          is_active: nextItem.is_active,
        },
      }));
      setSuccessMessage(`Sale location ${nextItem.code} was updated successfully.`);
    } catch (err) {
      const code = err instanceof Error ? err.message : "failed_to_update_sale_channel";
      setError(getErrorMessage(code));
    } finally {
      setUpdatingId(null);
    }
  }

  return (
    <div className="min-vh-100 bg-primary py-4 py-md-5">
      <div className="container">
        <div className="row justify-content-center">
          <div className="col-12 col-xl-10">
            <div className="card border-0 shadow-sm rounded-4">
              <div className="card-body p-4 p-md-5">
                <div className="d-flex flex-column flex-md-row justify-content-between align-items-md-center gap-3 mb-4">
                  <div>
                    <h2 className="text-primary mb-1">Sale Channels</h2>
                    <div className="text-muted">
                      Add, edit, and enable or disable sale locations used by the system.
                    </div>
                  </div>
                  <Link to="/" className="btn btn-outline-primary">
                    Back to Menu
                  </Link>
                </div>

                {error && <div className="alert alert-danger rounded-3">{error}</div>}
                {successMessage && (
                  <div className="alert alert-success rounded-3">{successMessage}</div>
                )}

                <div className="card border rounded-4 mb-4">
                  <div className="card-body">
                    <h5 className="text-primary mb-3">Create Sale Channel</h5>
                    <form className="row g-3 align-items-end" onSubmit={(e) => void handleCreate(e)}>
                      <div className="col-12 col-md-4">
                        <label className="form-label">Code</label>
                        <input
                          type="text"
                          className="form-control"
                          value={form.code}
                          onChange={(e) =>
                            setForm((prev) => ({ ...prev, code: e.target.value.toUpperCase() }))
                          }
                          placeholder="MAIN_SHOP"
                        />
                      </div>
                      <div className="col-12 col-md-5">
                        <label className="form-label">Display Name</label>
                        <input
                          type="text"
                          className="form-control"
                          value={form.label}
                          onChange={(e) =>
                            setForm((prev) => ({ ...prev, label: e.target.value }))
                          }
                          placeholder="ร้านหลัก"
                        />
                      </div>
                      <div className="col-12 col-md-3">
                        <div className="form-check form-switch mb-2">
                          <input
                            id="sale-channel-active"
                            type="checkbox"
                            className="form-check-input"
                            checked={form.is_active}
                            onChange={(e) =>
                              setForm((prev) => ({ ...prev, is_active: e.target.checked }))
                            }
                          />
                          <label className="form-check-label" htmlFor="sale-channel-active">
                            Active
                          </label>
                        </div>
                        <button type="submit" className="btn btn-primary w-100" disabled={saving}>
                          {saving ? "Saving..." : "Create"}
                        </button>
                      </div>
                    </form>
                  </div>
                </div>

                <div className="table-responsive">
                  <table className="table align-middle">
                    <thead>
                      <tr>
                        <th style={{ width: 180 }}>Code</th>
                        <th>Display Name</th>
                        <th style={{ width: 140 }}>Status</th>
                        <th className="text-end" style={{ width: 140 }}>Manage</th>
                      </tr>
                    </thead>
                    <tbody>
                      {loading ? (
                        <tr>
                          <td colSpan={4} className="text-center text-muted py-4">
                            Loading sale locations...
                          </td>
                        </tr>
                      ) : items.length === 0 ? (
                        <tr>
                          <td colSpan={4} className="text-center text-muted py-4">
                            No sale locations yet.
                          </td>
                        </tr>
                      ) : (
                        items.map((item) => {
                          const draft = drafts[item.id] ?? {
                            code: item.code,
                            label: item.label,
                            is_active: item.is_active,
                          };
                          const isUpdating = updatingId === item.id;

                          return (
                            <tr key={item.id}>
                              <td>
                                <input
                                  type="text"
                                  className="form-control"
                                  value={draft.code}
                                  onChange={(e) =>
                                    setDrafts((prev) => ({
                                      ...prev,
                                      [item.id]: {
                                        ...draft,
                                        code: e.target.value.toUpperCase(),
                                      },
                                    }))
                                  }
                                  disabled={isUpdating}
                                />
                              </td>
                              <td>
                                <input
                                  type="text"
                                  className="form-control"
                                  value={draft.label}
                                  onChange={(e) =>
                                    setDrafts((prev) => ({
                                      ...prev,
                                      [item.id]: {
                                        ...draft,
                                        label: e.target.value,
                                      },
                                    }))
                                  }
                                  disabled={isUpdating}
                                />
                              </td>
                              <td>
                                <div className="form-check form-switch">
                                  <input
                                    type="checkbox"
                                    className="form-check-input"
                                    checked={draft.is_active}
                                    onChange={(e) =>
                                      setDrafts((prev) => ({
                                        ...prev,
                                        [item.id]: {
                                          ...draft,
                                          is_active: e.target.checked,
                                        },
                                      }))
                                    }
                                    disabled={isUpdating}
                                  />
                                  <label className="form-check-label">
                                    {draft.is_active ? "Active" : "Inactive"}
                                  </label>
                                </div>
                              </td>
                              <td className="text-end">
                                <button
                                  type="button"
                                  className="btn btn-outline-primary btn-sm"
                                  onClick={() => void handleSave(item)}
                                  disabled={isUpdating}
                                >
                                  {isUpdating ? "Saving..." : "Save"}
                                </button>
                              </td>
                            </tr>
                          );
                        })
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
